import { normalizeDateString } from '@/lib/localeFormat';
import { toCountryOptions } from '@/lib/countryList';
import type { TFunction } from 'i18next';
import type { CountryInfoDto, UserInfoDto } from '@/types/api';
import type { User } from '@/types/admin';

/** 사용자 관리 목록·필터 공통 — 권한 4분류 (게스트 / 정회원 / 마스터 / 어드민) */
export type UserPermissionKind = 'guest' | 'regular_member' | 'master' | 'admin';

/** accountType·role·type 조합으로 권한 분류 (API/목록 변환 경로 차이 흡수) */
export function getUserPermissionKind(user: User): UserPermissionKind {
	const roleLower = String(user.role ?? '').toLowerCase();
	const isGuestAccount = user.accountType === '게스트' || user.type === 'guest';
	if (isGuestAccount || roleLower === 'guest') return 'guest';
	if (roleLower === 'master') return 'master';
	if (roleLower === 'admin') return 'admin';
	return 'regular_member';
}

/** 테이블·CSV 등에 쓰는 통합 권한 라벨 (역할·회원구분을 한 열로 표시) */
export function formatUsersPermissionLabel(user: User, t: TFunction<'users'>): string {
	switch (getUserPermissionKind(user)) {
		case 'guest':
			return t('accountTypeGuest');
		case 'regular_member':
			return t('accountTypeRegular');
		case 'master':
			return t('roleMaster');
		case 'admin':
			return t('roleAdmin');
		default:
			return '-';
	}
}

/** 사용자 관리 테이블 등 — accountType(정회원·게스트·regular·guest) 표시 문자열 */
export function formatUsersAccountTypeLabel(value: string | undefined, t: TFunction<'users'>): string {
	if (value === '정회원' || value === 'regular') return t('accountTypeRegular');
	if (value === '게스트' || value === 'guest') return t('accountTypeGuest');
	return value || '-';
}

/** 사용자 관리 테이블 등 — role(Master·Student·Admin·Guest) 표시 문자열 */
export function formatUsersRoleLabel(value: string | undefined, t: TFunction<'users'>): string {
	const role = String(value ?? '').toLowerCase();
	if (role === 'master') return t('roleMaster');
	if (role === 'student') return t('roleStudent');
	if (role === 'admin') return t('roleAdmin');
	if (role === 'guest') return t('accountTypeGuest');
	return value || '-';
}

export interface ConvertAdminUserOptions {
	apiCountryList?: CountryInfoDto[];
	locale?: string;
}

function coerceUserListCountryId(item: UserInfoDto): number | undefined {
	const raw = item as UserInfoDto & Record<string, unknown>;
	const nested =
		item.country && typeof item.country === 'object' ? (item.country as Record<string, unknown>) : null;
	const candidates: unknown[] = [raw.countryId, raw.country_id, nested?.countryId, nested?.country_id];
	for (const c of candidates) {
		if (typeof c === 'number' && Number.isFinite(c) && c >= 1) {
			return Math.floor(c);
		}
		if (typeof c === 'string') {
			const t = c.trim();
			if (/^\d+$/.test(t)) {
				const n = Number(t);
				if (Number.isFinite(n) && n >= 1) return Math.floor(n);
			}
		}
	}
	return undefined;
}

function extractAlpha2FromUserInfoDto(item: UserInfoDto): string | undefined {
	const nested =
		item.country && typeof item.country === 'object' ? (item.country as Record<string, unknown>) : null;
	const codeRaw = nested?.code ?? nested?.alpha2;
	if (typeof codeRaw !== 'string') return undefined;
	const c = codeRaw.trim().toUpperCase();
	if (/^[A-Z]{2}$/.test(c)) return c;
	return undefined;
}

/**
 * 설정(/settings) 국가 셀렉트와 동일한 라벨(toCountryOptions)로 표시 문자열 결정.
 */
export function resolveAdminUserCountryDisplay(
	item: UserInfoDto,
	apiCountryList: CountryInfoDto[],
	locale?: string,
): string | undefined {
	const options = toCountryOptions(apiCountryList, locale);

	const id = coerceUserListCountryId(item);
	if (id != null) {
		const found = options.find((o) => o.countryId === id);
		if (found) return found.title;
	}

	const alpha2 = extractAlpha2FromUserInfoDto(item);
	if (alpha2) {
		const found = options.find((o) => o.alpha2 === alpha2);
		if (found) return found.title;
	}

	const label = getCountryLabel(item)?.trim();
	if (!label) return undefined;

	const byTitle = options.find((o) => o.title === label);
	if (byTitle) return byTitle.title;

	for (const entry of apiCountryList) {
		const tKo = entry.title?.trim();
		const tEn = entry.titleEn?.trim();
		if ((tKo && label === tKo) || (tEn && label === tEn)) {
			return options.find((o) => o.countryId === entry.countryId)?.title;
		}
	}

	if (label.length === 2) {
		const upper = label.toUpperCase();
		const byCode = options.find((o) => o.alpha2 === upper);
		if (byCode) return byCode.title;
	}

	const lower = label.toLowerCase();
	const byTitleInsensitive = options.find((o) => o.title.toLowerCase() === lower);
	if (byTitleInsensitive) return byTitleInsensitive.title;

	return label;
}

export const CUSTOMER_TYPE_ORGANIZATION = '기관사용자';
export const CUSTOMER_TYPE_INDIVIDUAL = '개인사용자';

export function parseUserDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const date = new Date(normalizeDateString(value));
    return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDateInputValue(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function getTodayDateInputValue(): string {
    return formatDateInputValue(new Date());
}

export function getEarliestUserDateInputValue(users: User[], dateFilterType: string): string {
    const dates = users
        .map((user) => parseUserDate(dateFilterType === 'registeredDate' ? user.registeredDate : user.lastLogin))
        .filter((date): date is Date => date !== null);
    if (dates.length === 0) return '';
    return formatDateInputValue(new Date(Math.min(...dates.map((date) => date.getTime()))));
}

export function getCountryLabel(item: UserInfoDto): string | undefined {
    if (typeof item.countryName === 'string' && item.countryName.trim()) {
        return item.countryName.trim();
    }
    if (typeof item.country === 'string' && item.country.trim()) {
        return item.country.trim();
    }
    if (item.country && typeof item.country === 'object') {
        return item.country.title?.trim() || item.country.name?.trim() || undefined;
    }
    return undefined;
}

function getUserRoleLabel(role: UserInfoDto['role']): 'Master' | 'Student' | 'Admin' {
    if (role === 4 || role === 5) return 'Admin';
    if (role === 2) return 'Master';
    return 'Student';
}

/** API UserInfoDto -> 화면 User 변환 */
export const convertApiUserToUser = (item: UserInfoDto, opts?: ConvertAdminUserOptions): User => {
	let country = getCountryLabel(item);
	const list = opts?.apiCountryList;
	if (list && list.length > 0) {
		country = resolveAdminUserCountryDisplay(item, list, opts.locale) ?? country;
	}
	return {
	    id: String(item.userId),
	    name: item.name,
	    email: item.email,
	    country,
	    type: item.type === 'REGULAR' ? 'regular' : 'guest',
	    accountType: item.type === 'REGULAR' ? '정회원' : '게스트',
	    customerType: item.organizationName ? CUSTOMER_TYPE_ORGANIZATION : CUSTOMER_TYPE_INDIVIDUAL,
	    organizationName: item.organizationName,
	    role: getUserRoleLabel(item.role),
	    registeredDate: item.createdAt ?? undefined,
	    lastLogin: item.lastSigninAt ?? undefined,
	    status: item.status === false || item.isActive === false ? 'inactive' : 'active',
	    loginId: item.loginId,
	    simulationCount: item.scenarioCount,
	    classCount: item.classCount ?? item.activeClassCount,
	    department: item.department,
	    position: item.position,
	    jobTitle: item.jobTitle ?? undefined,
	    customJobTitle: item.customJobTitle ?? undefined,
	    affiliation: item.affiliation ?? undefined,
	    phoneNumber: item.phoneNumber,
	    expirationDate: item.guestAccountExpiresAt ? item.guestAccountExpiresAt.slice(0, 10) : undefined,
	};
};
